import moment from "moment";


export type YMD = { year: number, month: number, day: number };
export type DateInterval = { start_date: Date, end_date: Date };
export class TimeOperation
{
    static toDBFormat(date: Date): string
    {
        return date.toISOString().
            replace(/T/, ' ').
            replace(/\..+/, '');
    }
    static format(date: Date, format: string): string
    {
        return moment(date).format(format);
    }
    static toDate(year: number, month: number, day: number): Date
    {
        return new Date(`${year}/${month}/${day}`);
    }
    static toDateByWeek(year: number, week: number, firstDay: number): Date
    {
        if (!firstDay)
            firstDay = 0;
        let offset = new Date(year, 0, 1).getDay();
        return new Date(year, 0, 1 + firstDay - offset + (week - 1) * 7, 0, 0, 0, 0);
    }
    static fromDate(date: Date): YMD
    {
        let year = this.yearNumber(date);
        let month = this.monthNumber(date);
        let day = this.dayNumber(date);
        return { year, month, day };
    }
    static getLARange(date: Date): DateInterval
    {
        let start_date = this.hoursLater(8, date);
        let end_date = this.daysLater(1, start_date);
        return {
            start_date,
            end_date
        };
    }
    static diffInSecond(t1: Date, t2: Date, abs: boolean): number
    {
        let ans = (t1.getTime() - t2.getTime()) / 1000;
        if (abs)
            ans = Math.abs(ans);
        return ans;
    }
    static diffInMinute(t1: Date, t2: Date, abs: boolean): number
    {
        return this.diffInSecond(t1, t2, abs) / 60;
    }
    //year
    static yearNumber(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        return date.getUTCFullYear();
    }
    static beginningOfYear(date: Date | null = null): Date
    {
        date = this.beginningOfMonth(date);
        date.setUTCMonth(0);
        return date;
    }
    //month
    static monthNumber(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        return date.getUTCMonth() + 1;
    }
    static beginningOfMonth(date: Date | null = null): Date
    {
        date = this.beginningOfDay(date);
        date.setUTCDate(1);
        return date;
    }
    static endOfMonth(date: Date | null = null): Date
    {
        date = this.beginningOfMonth(date);
        date = this.monthsLater(1, date);
        date = this.secondsAgo(1, date);
        return date;
    }
    //week
    static weekNumber(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        let first = this.beginningOfYear(date);
        let secs = this.diffInSecond(date, first, true);
        return (secs / 60 / 60 / 24 / 7) + 1;
    }
    static beginningOfWeek(date: Date | null = null): Date
    {
        date = this.beginningOfDay(date);
        let day = (date.getDay() - 1) % 7;
        return this.daysAgo(day, date);
    }
    static endOfWeek(date: Date | null = null): Date
    {
        date = this.beginningOfWeek(date);
        return this.secondsAgo(1, this.daysLater(7, date));
    }
    // day
    static dayNumber(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        return date.getUTCDate();
    }
    static beginningOfDay(date: Date | null = null): Date
    {
        date = this.beginningOfHour(date);
        date.setUTCHours(0);
        return date;
    }
    static endOfDay(date: Date | null = null): Date
    {
        date = this.beginningOfDay(date);
        return this.daysLater(1, date);
    }
    static dayNumberInYear(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        var start = new Date(date.getFullYear(), 0, 0);
        var diff = date.getTime() - start.getTime();
        var oneDay = 1000 * 60 * 60 * 24;
        return Math.floor(diff / oneDay);
    }
    static addedDaysInMonth(days: number, date: Date): Date
    {
        date = this.beginningOfMonth(date);
        return this.daysLater(days, date);
    }
    //hour
    static hourNumberInWeek(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        let first = this.beginningOfWeek(date);
        let secs = this.diffInSecond(date, first, true);
        return (secs / 3600);
    }
    static hourNumberInMonth(date: Date | null = null): number
    {
        if (!date)
            date = new Date();
        let first = this.beginningOfMonth(date);
        let secs = this.diffInSecond(date, first, true);
        return (secs / 3600);
    }
    static addedHoursInWeek(hours: number, date: Date): Date
    {
        date = this.beginningOfWeek(date);
        return this.hoursLater(hours, date);
    }
    static beginningOfHour(date: Date | null = null): Date
    {
        if (!date)
            date = new Date();
        else
            date = new Date(date);
        date.setUTCMilliseconds(0);
        date.setUTCSeconds(0);
        date.setUTCMinutes(0);
        return date;
    }
    // ago
    static monthsAgo(months: number, date: Date): Date
    {
        return this.monthsLater(months * -1, date);
    }
    static daysAgo(days: number, date: Date): Date
    {
        return this.daysLater(days * -1, date);
    }
    static hoursAgo(hours: number, date: Date): Date
    {
        return this.hoursLater(hours * -1, date);
    }
    static minutesAgo(minutes: number, date: Date): Date
    {
        return this.minutesLater(minutes * -1, date);
    }
    static secondsAgo(seconds: number, date: Date): Date
    {
        return this.secondsLater(seconds * -1, date);
    }
    static millisecondsAgo(milliseconds: number, date: Date): Date
    {
        return this.millisecondsLater(milliseconds * -1, date);
    }
    // later
    static later(num: number, type: moment.DurationInputArg2, date: Date | null = null): Date
    {
        if (!date)
            date = new Date();
        else
            date = new Date(date);
        return moment(date).add(num, type).toDate();
    }
    static monthsLater(months: number, date: Date): Date
    {
        return this.later(months, 'months', date);
    }
    static daysLater(days: number, date: Date): Date
    {
        return this.later(days, 'days', date);
    }
    static hoursLater(hours: number, date: Date): Date
    {
        return this.later(hours, 'hours', date);
    }
    static minutesLater(minutes: number, date: Date): Date
    {
        return this.later(minutes, 'minutes', date);
    }
    static secondsLater(seconds: number, date: Date): Date
    {
        return this.later(seconds, 'seconds', date);
    }
    static millisecondsLater(milliseconds: number, date: Date): Date
    {
        return this.later(milliseconds, 'milliseconds', date);
    }
    static getDuration(startDate: Date, endDate: Date, zeroAllOnPassed: boolean = false)
    {
        if (!endDate)
            endDate = new Date();
        let s = moment(startDate);
        let e = moment(endDate);
        let passed = e < s;
        let year = e.diff(s, 'years', false);
        s.add(year, 'years');
        let month = e.diff(s, 'months', false);
        s.add(month, 'months');
        let day = e.diff(s, 'days', false);
        s.add(day, 'days');
        let hour = e.diff(s, 'hours', false);
        s.add(hour, 'hours');
        let minute = e.diff(s, 'minutes', false);
        s.add(minute, 'minutes');
        let second = e.diff(s, 'seconds', false);
        s.add(second, 'seconds');

        if (passed)
            if (zeroAllOnPassed)
                year = month = day = hour = minute = second = 0;
        let total = year * 12 + month;
        total = total * 30 + day;
        total = total * 24 + hour;
        total = total * 60 + minute;
        total += second;

        return {
            passed, year, month, day, hour, minute, second, total
        };
    }
    static calculateAge(date: Date): number
    {
        let birthday = new Date(date);
        let ageDifMs = Date.now() - birthday.getTime();
        var ageDate = new Date(ageDifMs); // miliseconds from epoch
        return Math.abs(ageDate.getUTCFullYear() - 1970);
    }
    static isValidDate(value: string): boolean
    {
        return moment(value).isValid();
    }
    static isValidDateTime(value: string): boolean
    {
        return moment(value).isValid();
    }
    static isValidTime(value: string): boolean
    {
        return moment(value).isValid();
    }
}